const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

interface ApiResponse<T> {
  data: T;
  ok: boolean;
  status: number;
}

interface ApiError {
  message: string;
  status: number;
}

function getToken(): string | null {
  return localStorage.getItem('auth_token');
}

function getTenantId(): string | null {
  return localStorage.getItem('tenant_id');
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const tenantId = getTenantId();
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({ message: res.statusText }));
    const error: ApiError = {
      message: errorBody.message ?? errorBody.error ?? res.statusText,
      status: res.status,
    };
    throw error;
  }

  const data = await res.json().catch(() => null);
  return { data: data as T, ok: true, status: res.status };
}

const api = {
  get<T>(path: string): Promise<ApiResponse<T>> {
    return request<T>('GET', path);
  },

  post<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>('POST', path, body);
  },

  put<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>('PUT', path, body);
  },

  patch<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>('PATCH', path, body);
  },

  delete<T>(path: string): Promise<ApiResponse<T>> {
    return request<T>('DELETE', path);
  },
};

export type { ApiResponse, ApiError };
export default api;
